/**
 * Cuenta la cantidad total de canciones en todas las playlists.
 * @param {Array} playlists - Lista de playlists.
 * @returns {number} - Cantidad total de canciones.
 */
export const contarCanciones = (playlists) => {
    if (!Array.isArray(playlists)) return 0;
    return playlists.reduce((total, { playlistData }) => total + (Array.isArray(playlistData) ? playlistData.length : 0), 0);
};

/**
 * Convierte un tiempo en formato "mm:ss" a segundos.
 * @param {string} timeString - Tiempo en formato "mm:ss".
 * @returns {number} - Tiempo en segundos.
 */
const convertirTiempoASegundos = (timeString) => {
    if (typeof timeString !== 'string' || !/^\d{1,2}:\d{2}$/.test(timeString)) {
        console.warn(`Formato incorrecto de trackTime: "${timeString}"`);
        return 0;
    }

    const [minutos, segundos] = timeString.split(':').map(Number);
    return (isNaN(minutos) || isNaN(segundos)) ? 0 : minutos * 60 + segundos;
};

/**
 * Calcula la duración total en minutos de una lista de canciones.
 * Ignora pistas sin trackTime válido.
 * @param {Array} songs - Lista de canciones con propiedad trackTime.
 * @returns {string} - Duración total en minutos.
 */
const calcularDuracion = (songs) => {
    if (!Array.isArray(songs)) return "0 min";

    const totalSegundos = songs.reduce((total, song) => {
        const trackTime = song?.trackTime?.trim();
        return total + (trackTime && /^\d{1,2}:\d{2}$/.test(trackTime) ? convertirTiempoASegundos(trackTime) : 0);
    }, 0);

    return `${Math.floor(totalSegundos / 60)} min`;
};

/**
 * Calcula la duración total de todas las playlists.
 * @param {Array} playlists - Lista de playlists.
 * @returns {string} - Duración total en minutos.
 */
export const calcularDuracionTotal = (playlists) => {
    if (!Array.isArray(playlists)) return "0 min";
    return calcularDuracion(playlists.flatMap(({ playlistData }) => playlistData || []));
};

/**
 * Calcula la duración de una sola playlist.
 * @param {Array} playlistData - Lista de canciones en la playlist.
 * @returns {string} - Duración total en minutos.
 */
export const calcularDuracionPlaylist = calcularDuracion;

/**
 * Carga un archivo de audio de forma asíncrona.
 * @param {string} url - URL del archivo de audio.
 * @returns {Promise<HTMLAudioElement>} - Objeto de audio cargado.
 */
export const loadAudio = async (url) => {
    if (typeof url !== 'string' || !url.trim()) {
        throw new Error("URL de audio inválida");
    }

    const audio = new Audio(url);
    return new Promise((resolve, reject) => {
        audio.oncanplaythrough = () => resolve(audio);
        audio.onerror = () => reject(new Error(`Error al cargar audio: ${url}`));
        try {
            audio.load();
        } catch (error) {
            reject(new Error(`Error inesperado al cargar audio: ${error.message}`));
        }
    });
};